import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Loader} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {greys} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {TextAreaCounter} from '../common/textAreaCounter';
import {DeviceTypesEnum, PlatformProps, withPlatform} from '../platform/platformContext';
import {SurveyStarsInput} from './surveyStarsInput';

/*
 * Props.
 */

export interface SurveyFormProps {
  submitForm(score: number, response?: string, contact?: string): void;
  includeContactInput: boolean;
  routeScore?: number;
  isFormSubmitting?: boolean;
}

/*
 * Constants.
 */

const maxResponseCharacters = 255;

/*
 * Style.
 */

export const surveyFormTextAreaWidth = 600;
const StyledTextAreaDiv = withPlatform(styled.div<PlatformProps>`
  width: ${(props) => (props.device === DeviceTypesEnum.DESKTOP ? `${surveyFormTextAreaWidth}px` : '100%')};
  position: relative;
`);

const FeedbackPrompt = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
`;

const StyledButton = styled(Button)`
  margin: 20px auto 5px auto;
`;

const StyledLoaderDiv = styled.div`
  margin-bottom: -20px;
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  min-height: 80px;
`;

const StyledLabelSpan = styled.span`
  font-size: ${fontSizes.medium};
  color: ${greys.shade80};
`;

const StyledEmailDiv = withPlatform(styled.div<PlatformProps>`
  margin: 20px auto 0px auto;
  width: ${(props) => (props.device === DeviceTypesEnum.DESKTOP ? '250px' : '75%')};
`);

/*
 * Component.
 */

export const SurveyForm: FC<SurveyFormProps> = ({
  submitForm,
  includeContactInput,
  routeScore,
  isFormSubmitting,
}) => {
  const [score, setScore] = useState<number | undefined>(routeScore);
  const [response, setResponse] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isTextCounterVisible, setIsTextCounterVisible] = useState<boolean>(false);

  const isSubmitDisabled = isFormSubmitting || isUndefined(score) || response.length > maxResponseCharacters;

  const onFormSubmit = () => {
    // Email is not currently required, so an empty email is
    // considered valid also
    const isCurrentEmailValid = !email || isEmail(email);
    if (!isUndefined(score) && isCurrentEmailValid) {
      submitForm(score, response, email);
      return;
    }

    // If we didn't submit the form succesfully, update the email state
    setIsValidEmail(isCurrentEmailValid);
  };

  const onTextAreaBlur = () => {
    // Only hide the counter if the current response is not
    // too long, otherwise keep it up to make the error state more clear
    if (response.length <= maxResponseCharacters) {
      setIsTextCounterVisible(false);
    }
  };

  return (
    <>
      <FeedbackPrompt>Give feedback</FeedbackPrompt>
      <SurveyStarsInput setScore={setScore} score={score} />
      <StyledTextAreaDiv>
        <StyledTextAreaBox
          key="textResponse"
          placeholder="Tell us more"
          size={VisualSizesEnum.MEDIUM}
          onChange={(value) => setResponse(value)}
          onFocus={() => {
            setIsTextCounterVisible(true);
          }}
          onBlur={onTextAreaBlur}
        />
        {isTextCounterVisible && (
          <TextAreaCounter textLength={response.length} textLengthLimit={maxResponseCharacters} />
        )}
      </StyledTextAreaDiv>
      {includeContactInput ? (
        <StyledEmailDiv>
          <TextBox
            placeholder="you@yourcompany.com"
            label={<StyledLabelSpan>Email</StyledLabelSpan>}
            size={VisualSizesEnum.MEDIUM}
            onChange={setEmail}
            hasError={!isValidEmail}
            errorMessage="Please enter a valid email"
          />
        </StyledEmailDiv>
      ) : null}
      <StyledButton
        variant={ButtonVariantsEnum.CUSTOM_DISABLED}
        isRounded
        size={VisualSizesEnum.MEDIUM}
        onClick={onFormSubmit}
        isDisabled={isSubmitDisabled}
      >
        Submit
      </StyledButton>
      {isFormSubmitting && (
        <StyledLoaderDiv>
          <Loader size={VisualSizesEnum.MEDIUM} />
        </StyledLoaderDiv>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function isEmail(value: string) {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value,
  );
}
